/**
 * Skill Sheet Service
 * 
 * エンジニアのスキルシートをExcel形式で生成・保存するサービス
 * 主な機能:
 * - エンジニア情報のスキルシート生成
 * - Excel形式でのダウンロード
 * - 複数エンジニアの一括生成
 * 
 * @service
 */

import ExcelJS from 'exceljs';
import { EngineerWithSkills, ApiResponse } from '../types';
import { engineersService } from './supabase';

export interface SkillSheetOptions {
  includePersonalInfo: boolean;
  includeProjectHistory: boolean;
  includeSkillDetails: boolean;
  templateStyle: 'standard' | 'detailed' | 'compact';
}

export class SkillSheetService {
  /**
   * 単一エンジニアのスキルシートを生成
   */
  static async generateSkillSheet(
    engineerId: number, 
    options: SkillSheetOptions = {
      includePersonalInfo: true,
      includeProjectHistory: true,
      includeSkillDetails: true,
      templateStyle: 'standard'
    }
  ): Promise<ApiResponse<Blob>> {
    try {
      const engineerResult = await engineersService.getById(engineerId);
      
      if (engineerResult.error || !engineerResult.data) {
        return { data: null, error: 'エンジニア情報の取得に失敗しました' };
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('スキルシート');

      await this.setupWorksheet(worksheet, engineerResult.data, options);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      return { data: blob, error: null };
    } catch (error) {
      return { data: null, error: 'スキルシートの生成に失敗しました' };
    }
  }

  /**
   * 複数エンジニアのスキルシートを一括生成
   */
  static async generateBulkSkillSheets(
    engineerIds: number[],
    options: SkillSheetOptions = {
      includePersonalInfo: true,
      includeProjectHistory: true,
      includeSkillDetails: true,
      templateStyle: 'standard'
    }
  ): Promise<ApiResponse<Blob>> {
    try {
      const workbook = new ExcelJS.Workbook();

      for (const engineerId of engineerIds) {
        const engineerResult = await engineersService.getById(engineerId);
        
        if (engineerResult.data) {
          const worksheet = workbook.addWorksheet(
            `${engineerResult.data.name.substring(0, 20)}_スキルシート`
          );
          await this.setupWorksheet(worksheet, engineerResult.data, options);
        }
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      return { data: blob, error: null };
    } catch (error) {
      return { data: null, error: '一括スキルシートの生成に失敗しました' };
    }
  }

  /**
   * ワークシートのセットアップ
   */
  private static async setupWorksheet(
    worksheet: ExcelJS.Worksheet, 
    engineer: EngineerWithSkills, 
    options: SkillSheetOptions
  ) {
    // タイトル設定
    worksheet.getCell('A1').value = 'スキルシート';
    worksheet.getCell('A1').font = { size: 18, bold: true };
    worksheet.mergeCells('A1:F1');
    worksheet.getCell('A1').alignment = { horizontal: 'center' };

    let currentRow = 3;

    // 基本情報セクション
    if (options.includePersonalInfo) {
      currentRow = await this.addPersonalInfoSection(worksheet, engineer, currentRow);
    }

    // スキル情報セクション
    if (options.includeSkillDetails) {
      currentRow = await this.addSkillsSection(worksheet, engineer, currentRow);
    }

    // プロジェクト履歴セクション（実装時にはproject_assignmentsテーブルから取得）
    if (options.includeProjectHistory) {
      void await this.addProjectHistorySection(worksheet, engineer, currentRow);
    }

    // セル幅の調整
    worksheet.getColumn('A').width = 20;
    worksheet.getColumn('B').width = 30;
    worksheet.getColumn('C').width = 15;
    worksheet.getColumn('D').width = 15;
    worksheet.getColumn('E').width = 20;
    worksheet.getColumn('F').width = 25;

    // 全体のスタイル設定
    this.applyGlobalStyles(worksheet, options.templateStyle);
  }

  /**
   * 基本情報セクションの追加
   */
  private static async addPersonalInfoSection(
    worksheet: ExcelJS.Worksheet, 
    engineer: EngineerWithSkills, 
    startRow: number
  ): Promise<number> {
    let row = startRow;

    // セクションタイトル
    worksheet.getCell(`A${row}`).value = '基本情報';
    worksheet.getCell(`A${row}`).font = { size: 14, bold: true };
    worksheet.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F3FF' } };
    worksheet.mergeCells(`A${row}:F${row}`);
    row += 2;

    // 基本情報の項目
    const personalInfo = [
      ['氏名', engineer.name],
      ['メールアドレス', engineer.email || '非公開'],
      ['電話番号', engineer.phone || '非公開'],
      ['経験年数', engineer.experience_years ? `${engineer.experience_years}年` : '未設定'],
      ['希望時給', engineer.hourly_rate ? `${engineer.hourly_rate.toLocaleString()}円` : '未設定'],
      ['稼働状況', engineer.availability_status === 'available' ? '稼働可能' : 
                   engineer.availability_status === 'busy' ? '稼働中' : '稼働不可'],
      ['居住地', engineer.location || '未設定'],
      ['リモートワーク', engineer.remote_work_preference ? '希望' : '希望しない'],
    ];

    personalInfo.forEach(([label, value]) => {
      worksheet.getCell(`A${row}`).value = label;
      worksheet.getCell(`B${row}`).value = value;
      worksheet.getCell(`A${row}`).font = { bold: true };
      row++;
    });

    // 自己紹介
    if (engineer.bio) {
      row++;
      worksheet.getCell(`A${row}`).value = '自己紹介';
      worksheet.getCell(`A${row}`).font = { bold: true };
      row++;
      worksheet.getCell(`A${row}`).value = engineer.bio;
      worksheet.mergeCells(`A${row}:F${row}`);
      worksheet.getCell(`A${row}`).alignment = { wrapText: true };
      row++;
    }

    return row + 2;
  }

  /**
   * スキル情報セクションの追加
   */
  private static async addSkillsSection(
    worksheet: ExcelJS.Worksheet, 
    engineer: EngineerWithSkills, 
    startRow: number
  ): Promise<number> {
    let row = startRow;

    // セクションタイトル
    worksheet.getCell(`A${row}`).value = 'スキル情報';
    worksheet.getCell(`A${row}`).font = { size: 14, bold: true };
    worksheet.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F3FF' } };
    worksheet.mergeCells(`A${row}:F${row}`);
    row += 2;

    // スキル表のヘッダー
    const headers = ['スキル名', 'レベル', '経験年数', '詳細'];
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(row, index + 1);
      cell.value = header;
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
    row++;

    // スキル情報
    if (engineer.skills && engineer.skills.length > 0) {
      engineer.skills.forEach(skill => {
        const levelMap = {
          'beginner': '初級',
          'intermediate': '中級',
          'advanced': '上級',
          'expert': 'エキスパート'
        };

        worksheet.getCell(`A${row}`).value = skill.skill_name;
        worksheet.getCell(`B${row}`).value = levelMap[skill.skill_level] || skill.skill_level;
        worksheet.getCell(`C${row}`).value = skill.years_experience ? `${skill.years_experience}年` : '-';
        worksheet.getCell(`D${row}`).value = `${skill.skill_level} レベル`;

        // 罫線を追加
        for (let col = 1; col <= 4; col++) {
          const cell = worksheet.getCell(row, col);
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        }
        row++;
      });
    } else {
      worksheet.getCell(`A${row}`).value = 'スキル情報が登録されていません';
      worksheet.mergeCells(`A${row}:D${row}`);
      row++;
    }

    return row + 2;
  }

  /**
   * プロジェクト履歴セクションの追加
   */
  private static async addProjectHistorySection(
    worksheet: ExcelJS.Worksheet, 
    engineer: EngineerWithSkills, 
    startRow: number
  ): Promise<number> {
    let row = startRow;

    // セクションタイトル
    worksheet.getCell(`A${row}`).value = 'プロジェクト履歴';
    worksheet.getCell(`A${row}`).font = { size: 14, bold: true };
    worksheet.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F3FF' } };
    worksheet.mergeCells(`A${row}:F${row}`);
    row += 2;

    // 実際の実装では、project_assignmentsテーブルからデータを取得
    worksheet.getCell(`A${row}`).value = 'プロジェクト履歴データの実装予定';
    worksheet.mergeCells(`A${row}:F${row}`);
    row++;

    return row + 2;
  }

  /**
   * 全体スタイルの適用
   */
  private static applyGlobalStyles(worksheet: ExcelJS.Worksheet, templateStyle: string) {
    // 追加のスタイリングロジックはここに実装
    // const styleConfig = {
    //   standard: {
    //     headerColor: 'FFE6F3FF',
    //     borderStyle: 'thin' as const
    //   },
    //   detailed: {
    //     headerColor: 'FFFFCC99',
    //     borderStyle: 'medium' as const
    //   },
    //   compact: {
    //     headerColor: 'FFF0F0F0',
    //     borderStyle: 'thin' as const
    //   }
    // };
    // const config = styleConfig[templateStyle as keyof typeof styleConfig] || styleConfig.standard;
  }

  /**
   * ファイルのダウンロード
   */
  static downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * 利用可能なエンジニア全員のスキルシートを生成
   */
  static async generateAllAvailableEngineersSkillSheet(
    options: SkillSheetOptions = {
      includePersonalInfo: true,
      includeProjectHistory: false,
      includeSkillDetails: true,
      templateStyle: 'compact'
    }
  ): Promise<ApiResponse<Blob>> {
    try {
      const availableEngineersResult = await engineersService.getAll();

      if (availableEngineersResult.error || !availableEngineersResult.data) {
        return { data: null, error: '利用可能なエンジニア情報の取得に失敗しました' };
      }

      const availableEngineers = availableEngineersResult.data.filter(engineer => 
        engineer.availability_status === 'available'
      );
      const engineerIds = availableEngineers.map(engineer => engineer.id);
      return await this.generateBulkSkillSheets(engineerIds, options);
    } catch (error) {
      return { data: null, error: '一括スキルシートの生成に失敗しました' };
    }
  }
}